import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerFooter,
  VStack,
  Text,
  Flex,
  Box,
  Divider,
  Button,
  useColorMode,
  useColorModeValue,
  IconButton,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Sun, Moon, ChevronDown, ChevronRight } from "lucide-react";
import { useState, type ElementType } from "react";

import { OnflyLogo } from "./OnflyLogo";
import { MENU_SECTIONS, isItemVisible } from "../config/menu";
import { hasRole } from "../lib/auth";
import { useLogout } from "../hooks/useLogout";
import { usePermissionsContext } from "../data/permissions/permissionContext";

const ONFLY_BLUE = "#009EFB";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode, toggleColorMode } = useColorMode();
  const { logout, loading: loggingOut } = useLogout();
  const drawerBg = useColorModeValue("white", "#0f1f3a");
  const sectionLabelColor = useColorModeValue("#6b7280", "#9ca3af");
  const itemHoverBg = useColorModeValue("rgba(0,158,251,0.06)", "rgba(0,158,251,0.1)");
  const itemColor = useColorModeValue("#0a0a0a", "#fff");
  const dividerColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const activeItemBg = useColorModeValue("rgba(0,158,251,0.08)", "rgba(0,158,251,0.12)");
  const footerBorderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const buttonHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const { permissions } = usePermissionsContext();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpand = (name: string) =>
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));

  const hasAnyPermission = (required: string[]) =>
    required.some((p) => permissions.includes(p));

  const visibleSections = MENU_SECTIONS.filter((section) => {
    if (section.requiredRole && !hasRole(section.requiredRole)) return false;
    if (section.requiredRoles?.length && !section.requiredRoles.some((r) => hasRole(r))) return false;
    if (section.requiredPermissions?.length && !hasAnyPermission(section.requiredPermissions)) return false;
    return true;
  });

  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent bg={drawerBg} maxW="260px">
        <DrawerCloseButton />
        <DrawerHeader
          pb={3}
          borderBottomWidth="1px"
          borderColor={dividerColor}
        >
          <Flex align="center" gap={3}>
            <OnflyLogo dark={colorMode === "dark"} height={28} variant="ds" />
            <Text fontSize="sm" fontWeight="700" letterSpacing="-0.01em">
              GG{" "}<Text as="span" color={ONFLY_BLUE}>Hub</Text>
            </Text>
          </Flex>
        </DrawerHeader>
        <DrawerBody py={3} px={3}>
          <VStack align="stretch" spacing={0}>
            {visibleSections.map((section, idx) => {
              const Icon = section.icon as ElementType;
              return (
                <Box key={section.name}>
                  {idx > 0 && (
                    <Divider my={3} borderColor={dividerColor} />
                  )}
                  <Flex align="center" gap={2} px={2} py={1} mb={1}>
                    <Box color={ONFLY_BLUE}>
                      <Icon size={13} />
                    </Box>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color={sectionLabelColor}
                      textTransform="uppercase"
                      letterSpacing="0.07em"
                    >
                      {section.name}
                    </Text>
                  </Flex>
                  <VStack align="stretch" spacing={0}>
                    {section.subitems?.filter((item) => isItemVisible(item, permissions)).map((item) => {
                      const ItemIcon = item.icon as ElementType | undefined;
                      const isActive = item.path === location.pathname;
                      const hasSubs = !!(item.subitems && item.subitems.length > 0);
                      const isOpen = !!expanded[item.name];
                      return (
                        <Box key={item.name}>
                          <Flex
                            align="center"
                            gap={2.5}
                            px={3}
                            py={2}
                            borderRadius="md"
                            cursor="pointer"
                            color={isActive ? ONFLY_BLUE : itemColor}
                            fontSize="sm"
                            fontWeight={isActive ? "600" : "500"}
                            bg={isActive ? activeItemBg : "transparent"}
                            transition="background 0.15s"
                            _hover={{ bg: itemHoverBg, color: ONFLY_BLUE }}
                            onClick={() => hasSubs ? toggleExpand(item.name) : (item.path && handleNavigate(item.path))}
                          >
                            {ItemIcon && (
                              <Box color={ONFLY_BLUE} opacity={isActive ? 1 : 0.7}>
                                <ItemIcon size={16} />
                              </Box>
                            )}
                            <Text flex={1}>{item.name}</Text>
                            {hasSubs && (
                              <Box color={ONFLY_BLUE} opacity={0.7}>
                                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </Box>
                            )}
                          </Flex>

                          {hasSubs && isOpen && (
                            <VStack align="stretch" spacing={0} pl={4} mt={1}>
                              {item.subitems!.filter((sub) => isItemVisible(sub, permissions)).map((sub) => {
                                const SubIcon = sub.icon as ElementType | undefined;
                                const subActive = sub.path === location.pathname;
                                return (
                                  <Flex
                                    key={sub.name}
                                    align="center"
                                    gap={2.5}
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    cursor="pointer"
                                    color={subActive ? ONFLY_BLUE : itemColor}
                                    fontSize="sm"
                                    fontWeight={subActive ? "600" : "500"}
                                    bg={subActive ? activeItemBg : "transparent"}
                                    transition="background 0.15s"
                                    _hover={{ bg: itemHoverBg, color: ONFLY_BLUE }}
                                    onClick={() => sub.path && handleNavigate(sub.path)}
                                  >
                                    {SubIcon && (
                                      <Box color={ONFLY_BLUE} opacity={subActive ? 1 : 0.7}>
                                        <SubIcon size={14} />
                                      </Box>
                                    )}
                                    <Text>{sub.name}</Text>
                                  </Flex>
                                );
                              })}
                            </VStack>
                          )}
                        </Box>
                      );
                    })}
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        </DrawerBody>
        <DrawerFooter
          borderTopWidth="1px"
          borderColor={footerBorderColor}
          py={3}
          px={3}
        >
          <Flex w="100%" gap={2}>
            <IconButton
              aria-label={colorMode === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
              icon={colorMode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              onClick={toggleColorMode}
              variant="ghost"
              size="sm"
              _hover={{ bg: buttonHoverBg }}
            />
            <Button
              leftIcon={<LogOut size={14} />}
              variant="ghost"
              size="sm"
              fontWeight="semibold"
              isLoading={loggingOut}
              loadingText="Saindo..."
              _hover={{ bg: buttonHoverBg, color: "red.500" }}
              onClick={logout}
              flex={1}
            >
              Sair
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
